import { Button, Stack, Typography } from "@mui/material";
import StatusChip from "./StatusChip";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import React, { useState } from "react";
import { useGetDUCInfoQuery, useGetOwnershipTransferStatusQuery, useCancelOwnershipTransferMutation, useGetIncomingOwnershipTransferQuery } from "../../../api/ducApiSlice";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useUserInfoQuery } from "../../../api/userApiSlice";
import { getRenewalStatus } from "../utils/renewalWindow";
import { setNewNotification } from "../../common/notificationSlice";
import TransferOwnershipModal from "./TransferOwnershipModal";


const GeneralBlockButtons = (props: any) => {

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { data: userData, refetch, isFetching: isUserFetching } = useUserInfoQuery({});
    const { data: ducDataObject, isLoading: isDucLoading, isFetching: isDucFetching } = useGetDUCInfoQuery({ email: userData?.email });
    const ducData = ducDataObject?.GENERAL?.[ducDataObject?.GENERAL.length - 1]
    const secondDucExists = ducDataObject?.GENERAL.length === 2

    // LC-6426: Training eligibility must track the most recent *Approved* GENERAL DUC, not simply
    // the newest record. Once a renewal is started, a brand-new Submitted/InProgress row becomes
    // the newest record while the original Approved (merely expiring) DUC is left untouched - that
    // untouched record is still the one that governs whether the user has a valid DUC to train
    // against. Mirrors the latestApproved scan in GeneralBlockComponent.tsx (which is how
    // props.expiring/props.expired are already derived).
    const latestApprovedGeneral = (ducDataObject?.GENERAL ?? []).slice().reverse()
        .find((g: any) => g?.status === 'Approved')
    const { data: transferStatus } = useGetOwnershipTransferStatusQuery(
        { ducDetailsId: ducData?.ducDetailsId },
        { skip: !ducData?.ducDetailsId }
    )
    const hasActiveTransfer: boolean = !!transferStatus?.transferId
    // The old PI may only cancel while the new PI's request has no signatures on it yet - once it
    // reaches InProgress, the new PI (or the Signing Official) has already signed.
    const canCancelTransfer: boolean = hasActiveTransfer && transferStatus?.newDucStatus !== "InProgress"
    const [cancelTransfer] = useCancelOwnershipTransferMutation();
    const [openTransferModal, setOpenTransferModal] = useState(false)

    // If the logged-in user is the designated new PI on a pending transfer (LC-221) and hasn't
    // submitted their own DUC yet, the normal Request access button should behave exactly like
    // IncomingOwnershipTransferBanner's "Begin My Application" - not require them to have noticed
    // and used the separate banner button.
    const { data: incomingTransfer } = useGetIncomingOwnershipTransferQuery({})
    const incomingTransferId: number | undefined = (incomingTransfer?.transferId && !incomingTransfer?.newDucDetailsId)
        ? incomingTransfer.transferId
        : undefined

    // Unified renewal status for accurate chip/time display
    const { daysLeft } = getRenewalStatus({
        status: ducData?.status,
        expiresOn: ducData?.expiresOn,
        isSub: ducDataObject?.subApplicantGeneral
    })

    const DucNotExpiredAndNotExpiring: boolean = !props.expired && !props.expiring
    const DucExpiringNotExpired: boolean = props.expiring && !props.expired
    const notSubApplicant: boolean = !ducDataObject?.subApplicantGeneral && ducDataObject?.subApplicantGeneral !== undefined
    const DucInProgressNotSignedByOfficial: boolean = (ducData?.status === "InProgress" || ducData?.status === "Submitted") && !ducData?.dateSignedOfficial
    const DucInProgress: boolean = ducData?.status !== "NotSubmitted" && ducData?.status !== "Approved" && ducData?.status !== "Declined"
    const DucApprovedTrainingComplete: boolean = ducData?.status === "Approved" && ducDataObject?.ducStatus === "TrainingComplete"
    const DucApprovedOrDeclinedTrainingComplete: boolean = (ducData?.status === "Approved" || ducData?.status === "Declined") && ducDataObject?.ducStatus === "TrainingComplete"
    const DucNotDeclinedOrSecondDucDoesExists: boolean = ducData?.status !== 'Declined' || secondDucExists

    // LC-6427: Sub-applicants should not see Renewal while the PI's DUC is merely approaching
    // expiration ("expiring") — only once it has actually expired can they see it (renewing is the
    // PI's action; a sub-applicant seeing it early just navigates into a read-only request view).
    // So hide Renewal for a sub-applicant whenever they have a DUC that's Submitted/InProgress, or
    // an Approved DUC that has not yet actually expired (expiring-but-not-expired still blocks).
    const hideRenewalForSub: boolean = ducDataObject?.subApplicantGeneral && ((ducDataObject?.GENERAL ?? []).some((g: any) => {
        if (g?.status === 'Submitted' || g?.status === 'InProgress') return true
        if (g?.status === 'Approved') {
            const { expired: gExpired } = getRenewalStatus({ status: g?.status, expiresOn: g?.expiresOn })
            return !gExpired
        }
        return false
    }))

    // For PIs too: once a renewal exists and is Active (InProgress/Submitted, or Approved-and-not-yet-expiring),
    // do not show another Renewal. An Approved renewal that has since expired, or has entered its own
    // expiring window, should NOT count as active — that's exactly when a new Renewal should be offered.
    const hasActiveRenewal: boolean = (ducDataObject?.GENERAL ?? []).some((g: any) => {
        if (g?.type !== 'Renewal') return false
        if (g?.status === 'InProgress' || g?.status === 'Submitted') return true
        if (g?.status === 'Approved') {
            const { expired: gExpired, expiring: gExpiring } = getRenewalStatus({ status: g?.status, expiresOn: g?.expiresOn })
            return !gExpired && !gExpiring
        }
        return false
    })

    // BE-driven overrides
    const isTrainingExpired: boolean = ducDataObject?.ducStatus === 'TrainingExpired'
    const isDucExpired: boolean = ducDataObject?.ducStatus === 'DucExpired'

    // If training will expire within the next 90 days, prompt user to re-start training
    // We assume training validity is 1 year from dateTrainingComplete
    const trainingCompletedAtStr = ducDataObject?.dateTrainingComplete as string | undefined
    const trainingCompletedAt = trainingCompletedAtStr ? new Date(trainingCompletedAtStr) : undefined
    const trainingExpiresAt = trainingCompletedAt ? new Date(trainingCompletedAt.getTime() + 365 * 24 * 60 * 60 * 1000) : undefined
    const now = new Date()
    const ninetyDaysFromNow = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000)

    // Explicit helpers for chip display
    const trainingIsExpired: boolean =
        isTrainingExpired ||
        (trainingExpiresAt != null && trainingExpiresAt.getTime() <= now.getTime());

    const trainingIsExpiring: boolean =
        !trainingIsExpired &&
        trainingExpiresAt != null &&
        trainingExpiresAt.getTime() <= ninetyDaysFromNow.getTime();

    // Training is already done and still valid — don't prompt to (re)start it.
    // Besides the explicit TrainingComplete status, a valid (unexpired) dateTrainingComplete also
    // counts: a sub-applicant added to a renewal can hold a stale 'Approved' status while their
    // own training is still valid (e.g. a former PI who swapped roles) — they must NOT be asked
    // to retrain. The expiring/expired cases below still re-enable the button when appropriate.
    const hasValidTrainingDate: boolean = trainingExpiresAt != null && !trainingIsExpired
    const isTrainingComplete: boolean = ducDataObject?.ducStatus === 'TrainingComplete' || hasValidTrainingDate

    // Renewal only takes precedence over Start Training once the DUC has actually expired
    // (or the server has marked it DucExpired) — a DUC that is merely approaching expiration
    // must still let the user start/finish training so it can be completed before it lapses.
    const renewalBlocksTraining: boolean = (
        (props.checkedCurrentStatus === true && !(isUserFetching || isDucLoading || isDucFetching)) &&
        ((props.expired || isDucExpired) &&
            !(ducData?.status === 'Submitted' && isDucExpired) &&
            !(ducData?.status === 'InProgress' && isDucExpired) &&
            !hideRenewalForSub &&
            !hasActiveRenewal)
    )

    // Updated rule (LC-6426): Show Start Training as long as there's a still-valid Approved GENERAL
    // DUC (training is only actionable against an approved cycle) and either training hasn't been
    // completed yet (first-time case) or is expired/expiring (renewal-of-training case). This must
    // remain true even while a Renewal request on top of it is Submitted/InProgress (pending SO or
    // Committee) - the original Approved DUC is still in force until it actually expires or the
    // renewal is decided (see renewalBlocksTraining, which already only kicks in on real expiry).
    const shouldShowStartTraining: boolean = !renewalBlocksTraining &&
        latestApprovedGeneral &&
        (!isTrainingComplete || trainingIsExpired || trainingIsExpiring);

    // Explicit per-status visibility for View Certificate / View Request, independent of the
    // broader toolbar gate below, so these render correctly for sub-applicants regardless of
    // whether the combined toolbar condition (showToolbarActions) evaluates true.
    // Approved -> View Certificate. InProgress or Submitted -> View Request (read-only for SAs,
    // since Edit/Cancel are already separately gated to PIs via `notSubApplicant`).
    const showViewCertificate: boolean = ducData?.status === 'Approved';
    const showViewRequest: boolean = ducData?.status === 'InProgress' || ducData?.status === 'Submitted';

    // Guard UI against flashing with stale/in-flight data
    const checkedAndFresh: boolean =
        props.checkedCurrentStatus === true &&
        !(isUserFetching || isDucLoading || isDucFetching);
    const setOldStatus = () => {
        localStorage.setItem('status', ducData?.status)
        // Clear the one-shot renewal flag so it can't leak into unrelated flows.
        // handleClickRenewal re-sets it AFTER this call when the special
        // "sub-applicant with DucExpired starts a renewal" case applies.
        localStorage.removeItem('skipPrevData')
        // Clear the one-shot ownership-transfer-accept flag (LC-221) so it can't leak into an
        // unrelated later action (a normal Request access, Edit, Renewal, or Add sub-applicant).
        localStorage.removeItem('ownershipTransferId')
    }

    // Mirrors the JSX condition that displays the main action toolbar
    const showToolbarActions: boolean = (
        ducData?.status  !== 'NotSubmitted' &&
        DucNotDeclinedOrSecondDucDoesExists &&
        (DucNotExpiredAndNotExpiring || (props.expiring && !props.expired) || (ducData?.status === 'Submitted' && isDucExpired) || (ducData?.status === 'InProgress' && isDucExpired)) &&
        props.checkedCurrentStatus
    )

    const handleClickGen = () => {
        setOldStatus()
        localStorage.setItem('edit', "false")
        localStorage.setItem('formType', "GENERAL")
        localStorage.setItem('AddSub', "false")

        // Accepting an ownership transfer (LC-221) is always a group DUC - skip the Individual/Group
        // selection modal and go straight into the same prefilled flow "Begin My Application" uses.
        if (incomingTransferId) {
            localStorage.setItem('isGroup', "true")
            localStorage.setItem('ownershipTransferId', String(incomingTransferId))
            navigate("/duc/request")
            return
        }

        if(ducData?.status === 'NotSubmitted' || ducData?.status === 'Declined'){
            props.setOpen(true)
        }
        else{
            navigate("/duc/request");
        }
    }

    // Renewal: always show the same selection modal as New Request (Individual vs Group)
    const handleClickRenewal = () => {
        setOldStatus()
        localStorage.setItem('edit', "false")
        localStorage.setItem('formType', "GENERAL")
        localStorage.setItem('AddSub', "false")

        // Special case: if the user is a sub-applicant and their DUC is marked DucExpired by BE,
        // treat renewal as an individual fresh request and do NOT pull old details.
        const isSubGeneral = !!ducDataObject?.subApplicantGeneral;
        const isDucExpired = ducDataObject?.ducStatus === 'DucExpired'
        if (isSubGeneral && isDucExpired) {
            // Do not pre-populate with old details; allow user to choose Individual or Group in the modal
            localStorage.setItem('skipPrevData', "true")
            props.setOpen(true)
            return
        } else {
            // For all other cases, ensure the special flag is cleared and show the selection modal
            localStorage.removeItem('skipPrevData')
        }

        // Do NOT set isGroup here; let the selection modal set it based on user choice
        props.setOpen(true)
    }

    const handleClickGenView = async () => {
        setOldStatus()
        localStorage.setItem('edit', "false")
        localStorage.setItem('formType', "GENERAL")
        localStorage.setItem('AddSub', "false")

        // If DUC is Approved, "View Certification" should open the finalized certificate/PDF
        if (ducData?.status === 'Approved' && ducData?.ducDetailsId) {
            try {
                const response = await fetch(`/api/duc/file/${ducData.ducDetailsId}`, { credentials: 'include' })
                if (!response.ok) throw new Error('Failed to retrieve certification')
                const json = await response.json()
                const base64 = json?.file
                if (!base64) throw new Error('Certification file is empty')

                // Convert base64 string to a Blob and open in a new tab
                const binaryString = atob(base64)
                const len = binaryString.length
                const bytes = new Uint8Array(len)
                for (let i = 0; i < len; i++) {
                    bytes[i] = binaryString.charCodeAt(i)
                }
                const blob = new Blob([bytes], { type: 'application/pdf' })
                const url = URL.createObjectURL(blob)
                window.open(url, '_blank')
                return
            } catch (e) {
                // If something goes wrong, fall back to the current in-app view flow
                console.error('View Certification failed:', e)
            }
        }
        // Default behavior (non-Approved statuses) or on fetch failure: open the request viewer at Review
        localStorage.setItem('viewCertification', 'true')
        navigate("/duc/request");
    }

    const handleClickEdit = () => {
        setOldStatus()
        localStorage.setItem('formType', "GENERAL")
        if(ducData?.group){
            localStorage.setItem('isGroup', "true")
        }
        else{
            localStorage.setItem('isGroup', "false")
        }
        localStorage.setItem('AddSub', "false")
        localStorage.setItem('edit', "true")
        navigate("/duc/request");
    }

    const handleClickTrain = () => {

        refetch().then(() => {
            // Ensure the status is updated in localStorage before navigating
            if (ducData?.status) {
                localStorage.setItem('status', ducData.status);
            }
            navigate("/duc/training");
        })
    };

    const handleClickViewSub = () => {
        setOldStatus()
        navigate("/duc/sub-applicants", { state: { "type": "GENERAL" } })
    }

    const handelCLickAddSub = () => {
        setOldStatus()
        localStorage.setItem('formType', "GENERAL")
        localStorage.setItem('AddSub', "true")
        localStorage.setItem('edit', "true")
        localStorage.setItem('isGroup', "true")
        navigate("/duc/request")
    }

    const handleClickDelete = () => {
        setOldStatus()
        props.setCancelRefresh(!props.cancelRefresh)
        props.setOpenDeleteModal(true);
    }

    const handleClickTransferOwnership = () => {
        setOpenTransferModal(true)
    }

    const handleClickCancelTransfer = () => {
        if (!transferStatus?.transferId) return
        cancelTransfer({ transferId: transferStatus.transferId }).then((response: any) => {
            if ("error" in response) {
                dispatch(setNewNotification({
                    message: response?.error?.data || "Unable to cancel Lead Investigator change. Please try again later.",
                    type: 'error'
                }))
            } else {
                dispatch(setNewNotification({
                    message: "Lead Investigator change cancelled.",
                    type: 'success'
                }))
            }
        })
    }

    // Main action/header row. Left: title + status chips. Right: context-aware action buttons
    return (
        <Stack
            flexDirection="row"
            justifyContent="space-between"
            gap={3}>
            {/* Left side: Header + Status chips reflecting current DUC/training state */}
            <Stack
                flexDirection="row"
                alignItems="center"
                gap={2}>
                {/* Product title */}
                <Typography fontSize="16px" fontWeight="600" variant="h2" id="general-use-header">
                    Data Use Certification for NIH Brain Development Cohorts
                </Typography>

                {/* Approved chip (shown only when DUC itself is Approved and not expired/expiring/training-expired) */}
                {(ducData?.status  === 'Approved') && DucNotExpiredAndNotExpiring && ducDataObject?.ducStatus !== 'Rejected' && !trainingIsExpired &&
                    <StatusChip state="approved"/>}

                {/* Training chips (only when DUC is Approved): show expiration or expiring soon */}
                {(ducData?.status === 'Approved' && trainingIsExpired) && (
                    <StatusChip state='trainingexpired' />
                )}
                {(ducData?.status === 'Approved' && !trainingIsExpired && trainingIsExpiring) && (
                    <StatusChip state='trainingexpiring' />
                )}

                {/* DUC time window chips (not about training): Expiring or Expired */}
                {DucExpiringNotExpired && <StatusChip state='expiring' time={daysLeft}/>}
                {props.expired && <StatusChip state='expired'/>}

                {/* BE-driven overall DUC rejection state */}
                {ducDataObject?.ducStatus === 'Rejected' && <StatusChip state='rejected'/>}

                {/* Special case: DocuSign envelope expired */}
                {ducData?.status === 'Declined' && ducData?.declinedBy === 'EnvelopeExpiry' && <StatusChip state='envelopeexpired'/>}

                {/* Ownership transfer in progress (LC-221) */}
                {hasActiveTransfer && <StatusChip state='ownershiptransfer'/>}

            </Stack>

            {/* Right side: Primary one-off actions when there's no existing request */}
            {ducData?.status === 'NotSubmitted' &&
                <Button
                    id="duc-general-request-access-button"
                    variant="primary"
                    onClick={handleClickGen}
                    sx={{ alignSelf: "center" }}
                >
                    Request access
                </Button>}

            {/* Allow creating a brand-new request when the last one was Declined, there's no 2nd DUC, and not server-expired */}
            {ducData?.status === 'Declined' && !secondDucExists && !isDucExpired &&
                <Button
                    id="duc-general-new-request-button"
                    variant="primary"
                    onClick={handleClickGen}
                    sx={{ alignSelf: "center" }}
                >
                    New Request
                </Button>}

            {/* Contextual action toolbar: displayed for most in-progress or active states */}
            {ducData?.status  !== 'NotSubmitted' && DucNotDeclinedOrSecondDucDoesExists && (DucNotExpiredAndNotExpiring || (props.expiring && !props.expired) || (ducData?.status === 'Submitted' && isDucExpired) || (ducData?.status === 'InProgress' && isDucExpired)) && props.checkedCurrentStatus &&

                <Stack
                    flexDirection="row"
                    gap={4}
                    alignItems={"center"}>
                    {/* View the current request (InProgress/Submitted) or the final certification (Approved) */}
                    {(showViewCertificate || showViewRequest) && (
                        <Button
                            id="duc-general-view-request-button"
                            variant="primary"
                            onClick={handleClickGenView}
                            sx={{ minWidth: "max-content" }}
                        >
                            {showViewCertificate ? "View Certification" : "View Request"}
                        </Button>
                    )}

                    {/* Edit only while InProgress/Submitted and before official signature; PIs only (no sub-applicants).
                        Disabled while an ownership transfer is in progress on this DUC. */}
                    {DucInProgressNotSignedByOfficial && notSubApplicant && !hasActiveTransfer &&
                        <Button
                            id="duc-general-edit-request-button"
                            variant="secondary"
                            onClick={handleClickEdit}
                            sx={{ minWidth: "max-content" }}
                        >
                            Edit Request
                        </Button>
                    }

                    {/* Cancel the current request if it's still InProgress (PIs only). Disabled while an
                        ownership transfer is in progress - cancel the transfer instead. */}
                    {DucInProgress && notSubApplicant && !hasActiveTransfer &&
                        <Button
                            id="duc-general-cancel-button"
                            variant="outlined"
                            color="error"
                            sx={{ minWidth: "max-content" }}
                            onClick={handleClickDelete}
                        >
                            Cancel Request
                        </Button>
                    }

                    {/* Training entry point: show when DUC is Approved or Training is expired, and no Renewal button is visible */}
                    {shouldShowStartTraining &&
                        <Button
                            id={"duc-general-start-training-button"}
                            variant="primary"
                            onClick={handleClickTrain}
                            sx={{ minWidth: "max-content" }}
                        >
                            Start Training
                        </Button>}

                    {/* Group management (PI only): view and add sub-applicants for group DUCs.
                        Training completion is required even while a renewal (second DUC record) is in flight. */}
                    {(DucApprovedTrainingComplete || (secondDucExists && ducDataObject?.ducStatus === "TrainingComplete")) && ducData.group && notSubApplicant &&
                        <Button
                            id="duc-general-view-subapplicant-button"
                            variant="primary"
                            onClick={handleClickViewSub}
                            sx={{ minWidth: "max-content" }}
                        >
                            View Sub-applicant(s)
                        </Button>}
                    {DucApprovedOrDeclinedTrainingComplete && ducData.group && notSubApplicant && !hasActiveTransfer &&
                        <Button
                            id="duc-general-add-subapplicant-button"
                            variant="primary"
                            startIcon={<AddBoxOutlinedIcon/>}
                            onClick={handelCLickAddSub}
                            sx={{ minWidth: "max-content" }}>
                            Add new Sub-applicant
                        </Button>}

                    {/* Change Lead Investigator (LC-221): only the current PI on an approved, trained
                        group DUC can start a transfer; once one is in progress, only Cancel shows. */}
                    {DucApprovedTrainingComplete && ducData.group && notSubApplicant && !hasActiveTransfer &&
                        <Button
                            id="duc-general-transfer-ownership-button"
                            variant="secondary"
                            onClick={handleClickTransferOwnership}
                            sx={{ minWidth: "max-content" }}>
                            Change Lead Investigator
                        </Button>}
                    {DucApprovedTrainingComplete && ducData.group && notSubApplicant && canCancelTransfer &&
                        <Button
                            id="duc-general-cancel-transfer-ownership-button"
                            variant="outlined"
                            color="error"
                            onClick={handleClickCancelTransfer}
                            sx={{ minWidth: "max-content" }}>
                            Cancel Lead Investigator Change
                        </Button>}
                </Stack>}

            {/* Standalone Start Training button when toolbar is hidden: show if DUC is Approved or Training is expired, and no Renewal button is visible */}
            {!showToolbarActions && shouldShowStartTraining &&
                <Button
                    id={"duc-general-start-training-button"}
                    variant="primary"
                    onClick={handleClickTrain}
                    sx={{ alignSelf: "center" }}
                >
                    Start Training
                </Button>}

            {/* Standalone View Certificate/View Request when toolbar is hidden: Approved -> Certificate, InProgress -> Request, existing SA gate preserved */}
            {!showToolbarActions && (showViewCertificate || showViewRequest) &&
                <Button
                    id="duc-general-view-request-button"
                    variant="primary"
                    onClick={handleClickGenView}
                    sx={{ alignSelf: "center" }}
                >
                    {showViewCertificate ? "View Certification" : "View Request"}
                </Button>}

            {/* Offer Renewal when renewal window is open or server marks DucExpired, provided there's no active renewal and sub-applicants are allowed */}
            {checkedAndFresh && ((props.expiring || props.expired || isDucExpired) && !(ducData?.status === 'Submitted' && isDucExpired) && !(ducData?.status === 'InProgress' && isDucExpired) && !hideRenewalForSub && !hasActiveRenewal && !hasActiveTransfer) &&
                <Button
                    id="duc-general-renewal-button"
                    variant="primary"
                    onClick={handleClickRenewal}
                    sx={{ alignSelf: "center" }}
                >
                    Renewal
                </Button>}

            {!!ducData?.ducDetailsId &&
                <TransferOwnershipModal open={openTransferModal} setOpen={setOpenTransferModal} ducDetailsId={ducData.ducDetailsId} />}

        </Stack>

    )
}

export default GeneralBlockButtons
